import { useEffect, useState } from "react";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";
import {
  apiPcf,
  type PcfBuscaLinha,
  type OpcaoProduto,
  type RoteiroLinha,
  type CombosDuplicar,
} from "../lib/api";
import { Typeahead } from "../components/Typeahead";
import { useTheme } from "../hooks/useTheme";
import { Logo } from "../components/Logo";
import { logout, type Usuario } from "../lib/auth";
import { MsgModal } from "../components/MsgModal";
import { mascaraDecimal } from "../lib/decimal";

// atcadpcf — duplicação de PCF, nas duas telas do legado:
//  * tela-duplicacao_nova (botão Duplicar do Planejamento): Nº da PCF ou nome do cliente (LIKE);
//    a cópia entra com numped 0 e abre a PCF nova.
//  * tela-duplicacao_nova_cli_pro (botão "Duplicar PCF" da PCF nova): já abre com o cliente e o
//    produto da PCF em digitação e pesquisa; "Por medidas" troca o filtro; a cópia herda o nº do
//    pedido em digitação, etiquetar = 1, e abre o roteiro de etapas da nova.
// + ver-etapas (tempo total) da PCF selecionada.

export interface OrigemCliPro {
  numped: number;
  codcli: number;
  codpro: number;
}

interface Props {
  usuario: Usuario;
  cliPro?: OrigemCliPro;
  onVoltar: () => void;
  onAbrirPcf: (numpcf: number) => void; // _nova: abre a PCF criada
  onEtapas?: (numpcf: number) => void; // cli_pro: abre as etapas da PCF criada
}

const nf = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2 = (n: number) => nf.format(n || 0);
// Medidas do filtro: máscara ZZZ.ZZ9,999 do form tela-duplicacao_nova_cli_pro -> até 3 casas
const limparDec = (v: string) => mascaraDecimal(v, 3);
const parseDec = (s: string) => Number(String(s).replace(",", ".")) || 0;

function totalTempo(linhas: RoteiroLinha[]): string {
  let min = 0;
  for (const l of linhas) {
    const t = Number(l.tempoEtapa) || 0;
    const h = Math.floor(t);
    min += h * 60 + Math.round((t - h) * 100);
  }
  return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}min`;
}

export function DuplicarPcf({ usuario, cliPro, onVoltar, onAbrirPcf, onEtapas }: Props) {
  const { theme, toggle } = useTheme();
  const ehCliPro = !!cliPro;
  const [combos, setCombos] = useState<CombosDuplicar | null>(null);
  const [clientes, setClientes] = useState<{ codigo: number; nomfan: string }[]>([]);

  // _nova
  const [numpcfOld, setNumpcfOld] = useState("");
  const [nomcliBusca, setNomcliBusca] = useState("");

  // cli_pro
  const [codcli, setCodcli] = useState(0); // 0 = Todos
  const [codpro, setCodpro] = useState(0);
  const [nompro, setNompro] = useState("");
  const [porMedidas, setPorMedidas] = useState(false);
  const [tipmat, setTipmat] = useState("");
  const [matpri, setMatpri] = useState("");
  const [aisi, setAisi] = useState("");
  const [costura, setCostura] = useState("");
  const [astm, setAstm] = useState("");
  const [largMin, setLargMin] = useState("");
  const [largMax, setLargMax] = useState("");
  const [espMin, setEspMin] = useState("");
  const [espMax, setEspMax] = useState("");
  const [diamMin, setDiamMin] = useState("");
  const [diamMax, setDiamMax] = useState("");

  const [linhas, setLinhas] = useState<PcfBuscaLinha[] | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [verEtapas, setVerEtapas] = useState<{ numpcf: number; linhas: RoteiroLinha[] } | null>(null);
  const ord = useOrdenacao(linhas, {
    cli: (l) => l.nomcli, pcf: (l) => l.numpcf, emi: (l) => l.datemi, pro: (l) => l.nompro, pec: (l) => l.qtdpec,
    cmin: (l) => l.compmin, cmax: (l) => l.compmax, met: (l) => l.qtdmet, peso: (l) => l.pesoTeorico,
  });
  const ordEt = useOrdenacao(verEtapas?.linhas, {
    pos: (l) => l.posicao, eta: (l) => l.descricao, desc: (l) => l.dimensionais, car: (l) => l.caracteristicas, dur: (l) => l.tempoEtapa,
  });

  const selecionada = sel != null && linhas ? linhas[sel] : null;
  const origemSel = ehCliPro ? selecionada?.numpcf ?? 0 : Number(numpcfOld) || 0;

  const [chaveProduto, setChaveProduto] = useState(0); // remonta o Typeahead (Limpar / Default)

  // CAMPOS-ORIGINAIS (tela-duplicacao_nova_cli_pro.prc L741-853): cliente/produto da PCF em
  // digitação, combos com as specs do produto, medidas em branco e desabilitadas, "Habilitar
  // Medidas" desmarcado e nova pesquisa. É o Opened (L1465) e o botão "Carregar Valores Default" (L484).
  async function camposOriginais(cli?: { codigo: number; nomfan: string }[]) {
    if (!cliPro) return;
    const specs = await apiPcf.duplicarProdutoSpecs(cliPro.codpro);
    const lista = cli ?? clientes;
    if (lista.some((c) => c.codigo === cliPro.codcli)) setCodcli(cliPro.codcli);
    setCodpro(cliPro.codpro);
    setNompro(`${cliPro.codpro} — ${specs.nompro}`);
    setChaveProduto((k) => k + 1);
    setTipmat(specs.tipmat);
    setMatpri(specs.matpri);
    setAisi(specs.aisi);
    setCostura(specs.costura);
    setAstm(specs.astm);
    for (const set of [setLargMin, setLargMax, setEspMin, setEspMax, setDiamMin, setDiamMax]) set("");
    setPorMedidas(false);
    await pesquisarCliPro({ codcli: cliPro.codcli, codpro: cliPro.codpro, porMedidas: false });
  }

  // cli_pro Opened (L1457-1467): combos + CAMPOS-ORIGINAIS.
  useEffect(() => {
    if (!ehCliPro || !cliPro) return;
    void (async () => {
      try {
        const [cmb, cli] = await Promise.all([apiPcf.duplicarCombos(), apiPcf.duplicarClientes()]);
        setCombos(cmb);
        setClientes(cli);
        await camposOriginais(cli);
      } catch (e) {
        setErro((e as Error).message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpar — _nova (tela-duplicacao_nova.prc L170-177) / cli_pro (tela-duplicacao_nova_cli_pro.prc L987-1014).
  function limpar() {
    setErro(null);
    setSel(null);
    setLinhas(null);
    if (!ehCliPro) {
      setNumpcfOld("");
      setNomcliBusca("");
      return;
    }
    setCodcli(0); // ListIndex 1 = "Todos"
    for (const set of [setTipmat, setMatpri, setAisi, setCostura, setAstm]) set("");
    setCodpro(0);
    setNompro("");
    setChaveProduto((k) => k + 1);
    for (const set of [setLargMin, setLargMax, setEspMin, setEspMax, setDiamMin, setDiamMax]) set("");
    setPorMedidas(false);
  }

  async function rodar(p: Parameters<typeof apiPcf.buscarDuplicar>[0]) {
    setErro(null);
    setSel(null);
    try {
      setCarregando(true);
      const r = await apiPcf.buscarDuplicar(p);
      if (r.length > 2000) {
        setErro("Quantidade de Itens é superior ao máximo permitido.");
        setLinhas(r.slice(0, 2000));
      } else setLinhas(r);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }

  // _nova T2-NOMCLI-WS LostFocus -> MONTAR-TABELA por nome (LIKE).
  function pesquisarNova() {
    void rodar({ nomcli: nomcliBusca.trim() });
  }

  // _nova T2-NUMPCF_OLD-WS LostFocus: nome do cliente da PCF; "PCF não encontrada!".
  async function conferirNumero() {
    const nro = Number(numpcfOld) || 0;
    if (!nro) return;
    const r = await apiPcf.duplicarDeCliente(nro);
    if (r.erro) {
      setErro(r.erro);
      return;
    }
    setErro(null);
    setNomcliBusca(r.nomcli);
  }

  // cli_pro MONTAR-TABELA: produto (+cliente) ou medidas (+cliente).
  async function pesquisarCliPro(o?: { codcli: number; codpro: number; porMedidas: boolean }) {
    const cli = (o?.codcli ?? codcli) || undefined;
    const med = o?.porMedidas ?? porMedidas;
    if (!med) {
      const pro = o?.codpro ?? codpro;
      if (!pro) return setErro("Informe Produto");
      return rodar({ codpro: pro, codcli: cli });
    }
    return rodar({
      porMedidas: true,
      codcli: cli,
      tipmat,
      matpri,
      aisi,
      costura,
      astm,
      largMin: parseDec(largMin) || undefined,
      largMax: parseDec(largMax) || undefined,
      espMin: parseDec(espMin) || undefined,
      espMax: parseDec(espMax) || undefined,
      diamMin: parseDec(diamMin) || undefined,
      diamMax: parseDec(diamMax) || undefined,
    });
  }

  async function gravar() {
    if (!origemSel || gravando) return;
    setErro(null);
    setGravando(true);
    try {
      const r = await apiPcf.duplicar(origemSel, cliPro ? { numped: cliPro.numped } : undefined);
      if (!r.ok) return setErro(r.mensagem);
      if (ehCliPro && onEtapas) onEtapas(r.numpcf);
      else onAbrirPcf(r.numpcf);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setGravando(false);
    }
  }

  async function abrirEtapas() {
    if (!origemSel) return;
    try {
      setVerEtapas({ numpcf: origemSel, linhas: await apiPcf.roteiro(origemSel) });
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  const Combo = ({ v, set, opcoes, dis }: { v: string; set: (s: string) => void; opcoes: string[]; dis?: boolean }) => (
    <select value={v} disabled={dis} onChange={(e) => set(e.target.value)}>
      <option value="">— selecione —</option>
      {opcoes.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );

  const enterPesquisa = (fn: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      fn();
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <Logo />
          <h1>
            Duplicação de PCF
            <small>{ehCliPro ? "Por cliente/produto" : "PCF"}</small>
          </h1>
        </div>
        <span className="spacer" />
        <div className="user-badge" title={`Usuário: ${usuario.nomusu}`}>
          <span className="user-avatar">{usuario.apelid.charAt(0).toUpperCase()}</span>
          <span className="user-nome">{usuario.nomusu.split(" ")[0]}</span>
        </div>
        <button className="btn-secundario" onClick={onVoltar}>
          {ehCliPro ? "← PCF" : "← PCP"}
        </button>
        <button className="btn-secundario" onClick={toggle} title={theme === "light" ? "Modo escuro" : "Modo claro"}>{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
        <button className="btn-secundario" onClick={logout} title="Sair">Sair</button>
      </header>

      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />

      <div className="pcf-form">
        <section className="pcf-sec">

          {!ehCliPro && (
            <div className="pcf-grid">
              <label className="pcf-campo pcf-sm">
                <span>PCF a ser copiada</span>
                <input
                  className="num"
                  inputMode="numeric"
                  value={numpcfOld}
                  onChange={(e) => setNumpcfOld(e.target.value.replace(/\D/g, ""))}
                  onBlur={() => void conferirNumero()}
                  onKeyDown={enterPesquisa(() => void conferirNumero())}
                />
              </label>
              <label className="pcf-campo pcf-lg">
                <span>Nome do Cliente</span>
                <input
                  value={nomcliBusca}
                  onChange={(e) => setNomcliBusca(e.target.value)}
                  onBlur={pesquisarNova}
                  onKeyDown={enterPesquisa(pesquisarNova)}
                  placeholder="Parte do nome — Enter pesquisa"
                />
              </label>
            </div>
          )}

          {ehCliPro && (
            <>
              <div className="pcf-grid">
                <label className="pcf-campo pcf-lg">
                  <span>Cliente</span>
                  <select value={codcli} onChange={(e) => setCodcli(Number(e.target.value))}>
                    <option value={0}>Todos</option>
                    {clientes.map((c) => (
                      <option key={c.codigo} value={c.codigo}>
                        {c.nomfan}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="pcf-campo pcf-lg">
                  <span>Produto</span>
                  <Typeahead<OpcaoProduto>
                    key={chaveProduto}
                    placeholder="Nome ou código…"
                    valorInicial={nompro}
                    buscar={apiPcf.buscarProduto}
                    rotulo={(p) => `${p.codpro} — ${p.nompro}`}
                    onEscolher={(p) => {
                      setCodpro(p.codpro);
                      setNompro(`${p.codpro} — ${p.nompro}`);
                    }}
                  />
                </label>
                <label className="pcf-campo pcf-sm pcf-check">
                  <span>Habilitar Medidas</span>
                  <input type="checkbox" checked={porMedidas} onChange={(e) => setPorMedidas(e.target.checked)} />
                </label>
              </div>
              <div className="pcf-grid">
                <label className="pcf-campo">
                  <span>Tipo de Material</span>
                  <Combo v={tipmat} set={setTipmat} opcoes={combos?.tipmat ?? []} dis={!porMedidas} />
                </label>
                <label className="pcf-campo">
                  <span>Matéria Prima</span>
                  <Combo v={matpri} set={setMatpri} opcoes={combos?.matpri ?? []} dis={!porMedidas} />
                </label>
                <label className="pcf-campo">
                  <span>AISI</span>
                  <Combo v={aisi} set={setAisi} opcoes={combos?.aisi ?? []} dis={!porMedidas} />
                </label>
                <label className="pcf-campo">
                  <span>Costura</span>
                  <Combo v={costura} set={setCostura} opcoes={combos?.costuras ?? []} dis={!porMedidas} />
                </label>
                <label className="pcf-campo">
                  <span>ASTM</span>
                  <Combo v={astm} set={setAstm} opcoes={combos?.astm ?? []} dis={!porMedidas} />
                </label>
              </div>
              <div className="pcf-grid">
                {(
                  [
                    ["Largura (Mín.)", largMin, setLargMin],
                    ["Largura (Máx.)", largMax, setLargMax],
                    ["Espessura (Mín.)", espMin, setEspMin],
                    ["Espessura (Máx.)", espMax, setEspMax],
                    ["Diâm. Externo (Mín.)", diamMin, setDiamMin],
                    ["Diâm. Externo (Máx.)", diamMax, setDiamMax],
                  ] as const
                ).map(([rot, v, setV]) => (
                  <label key={rot} className="pcf-campo pcf-sm">
                    <span>{rot}</span>
                    <input
                      className="num"
                      inputMode="decimal"
                      value={v}
                      disabled={!porMedidas}
                      onChange={(e) => setV(limparDec(e.target.value))}
                    />
                  </label>
                ))}
              </div>
              <div className="pcf-acoes">
                <button className="btn-acao" onClick={() => void pesquisarCliPro()}>
                  Pesquisar
                </button>
                <button className="btn-secundario" onClick={limpar}>
                  Limpar
                </button>
                <button className="btn-secundario" onClick={() => void camposOriginais().catch((e) => setErro((e as Error).message))}>
                  Carregar Valores Default
                </button>
              </div>
            </>
          )}
        </section>

        <section className="pcf-sec">
          <div className="tabela-wrap">
            <table className="tabela-pcfs">
              <thead>
                {ehCliPro ? (
                  <tr>
                    <ThOrdena ord={ord} col="cli">Cliente</ThOrdena>
                    <ThOrdena ord={ord} col="pcf" className="num">Nº PCF</ThOrdena>
                    <ThOrdena ord={ord} col="emi">Dt. Emissão</ThOrdena>
                    <ThOrdena ord={ord} col="pro">Produto</ThOrdena>
                    <ThOrdena ord={ord} col="pec" className="num">Peças</ThOrdena>
                    <ThOrdena ord={ord} col="cmin" className="num">Comp. Mín.</ThOrdena>
                    <ThOrdena ord={ord} col="cmax" className="num">Comp. Máx.</ThOrdena>
                    <ThOrdena ord={ord} col="met" className="num">Qtd. Metros</ThOrdena>
                    <ThOrdena ord={ord} col="peso" className="num">Peso Teórico</ThOrdena>
                  </tr>
                ) : (
                  <tr>
                    <ThOrdena ord={ord} col="pcf" className="num">Nº da PCF</ThOrdena>
                    <ThOrdena ord={ord} col="cli">Cliente</ThOrdena>
                    <ThOrdena ord={ord} col="pro">Produto</ThOrdena>
                    <ThOrdena ord={ord} col="emi">Dt. Emissão</ThOrdena>
                  </tr>
                )}
              </thead>
              <tbody>
                {carregando && (
                  <tr>
                    <td colSpan={9} className="plan-vazio">
                      Carregando…
                    </td>
                  </tr>
                )}
                {!carregando && linhas && linhas.length === 0 && (
                  <tr>
                    <td colSpan={9} className="plan-vazio">
                      Nenhuma PCF encontrada.
                    </td>
                  </tr>
                )}
                {!carregando &&
                  linhas &&
                  ord.linhas.map((l) => { const i = ord.idx(l); return (
                    <tr
                      key={l.numpcf}
                      className={"row-clickable" + (sel === i ? " plan-sel" : "")}
                      onClick={() => {
                        setSel(i);
                        if (!ehCliPro) {
                          setNumpcfOld(String(l.numpcf));
                          setNomcliBusca(l.nomcli);
                        }
                      }}
                    >
                      {ehCliPro ? (
                        <>
                          <td>{l.nomcli}</td>
                          <td className="num">{l.numpcf}</td>
                          <td>{l.datemi ?? ""}</td>
                          <td>{l.nompro}</td>
                          <td className="num">{fmt2(l.qtdpec)}</td>
                          <td className="num">{fmt2(l.compmin)}</td>
                          <td className="num">{fmt2(l.compmax)}</td>
                          <td className="num">{fmt2(l.qtdmet)}</td>
                          <td className="num">{fmt2(l.pesoTeorico)}</td>
                        </>
                      ) : (
                        <>
                          <td className="num">{l.numpcf}</td>
                          <td>{l.nomcli}</td>
                          <td>{l.nompro}</td>
                          <td>{l.datemi ?? ""}</td>
                        </>
                      )}
                    </tr>
                  ); })}
              </tbody>
            </table>
          </div>
        </section>

        <div className="pcf-acoes">
          <button className="btn-acao" disabled={!origemSel || gravando} onClick={() => void gravar()}>
            {gravando ? "Gravando…" : "Confirmar"}
          </button>
          {ehCliPro ? (
            <button className="btn-secundario" disabled={!origemSel} onClick={() => void abrirEtapas()}>
              Etapas
            </button>
          ) : (
            <button className="btn-secundario" onClick={limpar}>
              Limpar
            </button>
          )}
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>
            Sair
          </button>
        </div>
      </div>

      {verEtapas && (
        <div className="pcf-modal-bg" onClick={() => setVerEtapas(null)}>
          <div className="pcf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pcf-modal-topo">
              <b>Plano de Controle de Fabricação - Etapas da Produção — PCF nº {verEtapas.numpcf}</b>
              <span className="spacer" />
              <small className="pcf-hint">Tempo Total (hh,mm): {totalTempo(verEtapas.linhas)}</small>
              <button className="btn-secundario" onClick={() => setVerEtapas(null)}>Voltar</button>
            </div>
            <div className="tabela-wrap">
              <table className="tabela-pcfs">
                <thead>
                  <tr>
                    <ThOrdena ord={ordEt} col="pos" className="num">Posição</ThOrdena>
                    <ThOrdena ord={ordEt} col="eta">Etapa</ThOrdena>
                    <ThOrdena ord={ordEt} col="desc">Descrição</ThOrdena>
                    <ThOrdena ord={ordEt} col="car">Características</ThOrdena>
                    <ThOrdena ord={ordEt} col="dur" className="num">Duração</ThOrdena>
                  </tr>
                </thead>
                <tbody>
                  {verEtapas.linhas.length === 0 && (
                    <tr>
                      <td colSpan={5} className="plan-vazio">
                        Sem etapas.
                      </td>
                    </tr>
                  )}
                  {ordEt.linhas.map((l) => (
                    <tr key={`${l.posicao}-${l.etapa}`}>
                      <td className="num">{l.posicao}</td>
                      <td>{l.descricao}</td>
                      <td>{l.dimensionais}</td>
                      <td>{l.caracteristicas}</td>
                      <td className="num">{String(l.tempoEtapa).replace(".", ",")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
